import { 
  collection, 
  doc, 
  setDoc, 
  getDocs, 
  updateDoc, 
  writeBatch,
  query,
  orderBy,
  limit,
  serverTimestamp,
  deleteField
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { 
  RoutineAssignment, 
  RoutineAssignmentFormData,
  AssignmentStatusValue
} from '../types/alarm';
import { 
  RoutineAssignmentConsolidated,
  RoutineDateAssignment,
  RoutineAssignmentConsolidatedV2,
  RoutineDateAssignmentV2,
  RoutineStudentStatusV2,
  RoutineStudentStatus
} from '../types/index';
import { normalizeMemberGroup, withMemberGroup } from '../utils/memberGroupPaths';

// ===== 배열 ↔ 중첩 객체 변환 유틸리티 =====

/**
 * 배열 형태의 studentStatuses를 중첩 객체로 변환
 */
function studentStatusesArrayToObject(
  arr: RoutineStudentStatus[]
): { [studentId: string]: RoutineStudentStatusV2 } {
  const obj: { [studentId: string]: RoutineStudentStatusV2 } = {};
  for (const status of arr) {
    const { studentId, ...rest } = status;
    obj[studentId] = rest;
  }
  return obj;
}

/**
 * 중첩 객체 형태의 studentStatuses를 배열로 변환 (하위 호환성)
 */
function studentStatusesObjectToArray(
  obj: { [studentId: string]: RoutineStudentStatusV2 }
): RoutineStudentStatus[] {
  return Object.entries(obj).map(([studentId, status]) => ({
    studentId,
    ...status
  }));
}

/**
 * 배열 형태의 dateAssignments를 중첩 객체로 변환
 */
function dateAssignmentsArrayToObject(
  arr: RoutineDateAssignment[]
): { [dateId: string]: RoutineDateAssignmentV2 } {
  const obj: { [dateId: string]: RoutineDateAssignmentV2 } = {};
  for (const dateAssignment of arr) {
    const { date, studentStatuses, ...rest } = dateAssignment;
    obj[date] = {
      ...rest,
      studentStatuses: studentStatuses ? studentStatusesArrayToObject(studentStatuses) : undefined
    };
  }
  return obj;
}

/**
 * 중첩 객체 형태의 dateAssignments를 배열로 변환 (하위 호환성)
 */
function dateAssignmentsObjectToArray(
  obj: { [dateId: string]: RoutineDateAssignmentV2 }
): RoutineDateAssignment[] {
  return Object.entries(obj).map(([date, dateAssignment]) => ({
    date,
    ...dateAssignment,
    studentStatuses: dateAssignment.studentStatuses 
      ? studentStatusesObjectToArray(dateAssignment.studentStatuses)
      : undefined
  }));
}

/**
 * 데이터가 배열인지 객체인지 확인
 */
function isArrayData(data: any): boolean {
  return Array.isArray(data);
}

const ROUTINE_CACHE_PREFIX = 'ra_';

/**
 * 루틴 과제 통합 문서 서비스
 * 하나의 문서에 모든 날짜별 데이터를 포함하는 새로운 구조
 */
export class RoutineAssignmentConsolidatedService {
  private readonly maxDocumentSize = 900000; // 900KB
  private readonly documentPrefix = 'DateAssignment-';
  private readonly memberGroup: string;
  private readonly rootCollectionName: string;

  private static __consolidatedCache = new Map<string, { v1: RoutineAssignmentConsolidated[]; v2: RoutineAssignmentConsolidatedV2[] }>();

  private getCacheKey(routineId: string): string {
    return `${ROUTINE_CACHE_PREFIX}${this.userId}_${this.classId}_${this.rootCollectionName}_${routineId}`;
  }

  private invalidateConsolidatedCache(routineId: string): void {
    RoutineAssignmentConsolidatedService.__consolidatedCache.delete(this.getCacheKey(routineId));
  }

  /** 삭제 등 이후 외부에서 캐시 무효화용 */
  static invalidateCacheForRoutine(userId: string, classId: string, memberGroup: string | undefined, routineId: string): void {
    const root = withMemberGroup('routineAssignmentStatuses', normalizeMemberGroup(memberGroup));
    const key = `${ROUTINE_CACHE_PREFIX}${userId}_${classId}_${root}_${routineId}`;
    RoutineAssignmentConsolidatedService.__consolidatedCache.delete(key);
  }

  constructor(
    private userId: string,
    private classId: string,
    memberGroup?: string
  ) {
    this.memberGroup = normalizeMemberGroup(memberGroup);
    this.rootCollectionName = withMemberGroup('routineAssignmentStatuses', this.memberGroup);
  }

  /**
   * 현재 활성 문서 번호 가져오기
   */
  private async getCurrentDocumentNumber(routineId: string): Promise<number> {
    const documentsQuery = query(
      collection(db, `users/${this.userId}/classes/${this.classId}/${this.rootCollectionName}/${routineId}/DateAssignment`),
      orderBy('documentNumber', 'desc'),
      limit(1)
    );
    
    const snapshot = await getDocs(documentsQuery);
    if (snapshot.empty) {
      return 1; // 첫 번째 문서
    }
    
    const lastDoc = snapshot.docs[0].data() as RoutineAssignmentConsolidated;
    return lastDoc.documentNumber;
  }

  /**
   * 모든 통합 문서 가져오기 (캐시 사용; 쓰기 시 무효화)
   */
  async getAllConsolidatedDocuments(routineId: string): Promise<RoutineAssignmentConsolidated[]> {
    const key = this.getCacheKey(routineId);
    const cached = RoutineAssignmentConsolidatedService.__consolidatedCache.get(key);
    if (cached) return cached.v1;

    const collectionRef = collection(db, `users/${this.userId}/classes/${this.classId}/${this.rootCollectionName}/${routineId}/DateAssignment`);
    const snapshot = await getDocs(collectionRef);
    const v1 = snapshot.docs.map(docSnap => {
      const data = docSnap.data();
      let dateAssignmentsArray: RoutineDateAssignment[] = [];
      if (data.dateAssignments) {
        if (isArrayData(data.dateAssignments)) dateAssignmentsArray = data.dateAssignments;
        else dateAssignmentsArray = dateAssignmentsObjectToArray(data.dateAssignments);
      }
      return {
        ...data,
        id: docSnap.id,
        _docId: docSnap.id,
        assignedStudentIds: data.assignedStudentIds || [],
        basePoints: data.basePoints || 10,
        classId: data.classId || this.classId,
        routineAssignmentId: data.routineAssignmentId || routineId,
        color: data.color || "#6366f1",
        dueDate: data.dueDate || new Date().toISOString().split('T')[0],
        title: data.title || '',
        description: data.description || '',
        startDate: data.startDate || new Date().toISOString().split('T')[0],
        routineDays: data.routineDays || [],
        categoryId: data.categoryId || 'routine',
        icon: data.icon || '',
        notes: data.notes || '',
        isActive: data.isActive !== undefined ? data.isActive : true,
        rewardEnabled: data.rewardEnabled ?? false,
        rewardMessage: data.rewardMessage || '',
        rewardCoins: data.rewardCoins ?? 0,
        rewardItemCardIds: data.rewardItemCardIds || [],
        rewardGrantedStudentIds: data.rewardGrantedStudentIds || [],
        rewardCompletionRate: data.rewardCompletionRate ?? 100,
        includeInIncompleteCheck: data.includeInIncompleteCheck ?? false,
        lastUpdated: data.lastUpdated?.toDate() || new Date(),
        createdAt: data.createdAt?.toDate() || new Date(),
        documentNumber: data.documentNumber || 1,
        totalSize: data.totalSize || 0,
        dateAssignments: dateAssignmentsArray
      };
    }) as RoutineAssignmentConsolidated[];
    const v2 = snapshot.docs.map(docSnap => {
      const data = docSnap.data();
      let dateAssignmentsObj: { [dateId: string]: RoutineDateAssignmentV2 } = {};
      if (data.dateAssignments) {
        if (isArrayData(data.dateAssignments)) dateAssignmentsObj = dateAssignmentsArrayToObject(data.dateAssignments);
        else dateAssignmentsObj = data.dateAssignments;
      }
      return {
        ...data,
        id: docSnap.id,
        lastUpdated: data.lastUpdated?.toDate() || new Date(),
        createdAt: data.createdAt?.toDate() || new Date(),
        dateAssignments: dateAssignmentsObj
      };
    }) as RoutineAssignmentConsolidatedV2[];
    RoutineAssignmentConsolidatedService.__consolidatedCache.set(key, { v1, v2 });
    return v1;
  }

  /**
   * 모든 통합 문서 가져오기 V2 (캐시 사용; 쓰기 시 무효화)
   */
  async getAllConsolidatedDocumentsV2(routineId: string): Promise<RoutineAssignmentConsolidatedV2[]> {
    const key = this.getCacheKey(routineId);
    const cached = RoutineAssignmentConsolidatedService.__consolidatedCache.get(key);
    if (cached) return cached.v2;

    const collectionRef = collection(db, `users/${this.userId}/classes/${this.classId}/${this.rootCollectionName}/${routineId}/DateAssignment`);
    const snapshot = await getDocs(collectionRef);
    const v1 = snapshot.docs.map(docSnap => {
      const data = docSnap.data();
      let dateAssignmentsArray: RoutineDateAssignment[] = [];
      if (data.dateAssignments) {
        if (isArrayData(data.dateAssignments)) dateAssignmentsArray = data.dateAssignments;
        else dateAssignmentsArray = dateAssignmentsObjectToArray(data.dateAssignments);
      }
      return {
        ...data,
        id: docSnap.id,
        _docId: docSnap.id,
        assignedStudentIds: data.assignedStudentIds || [],
        basePoints: data.basePoints || 10,
        classId: data.classId || this.classId,
        routineAssignmentId: data.routineAssignmentId || routineId,
        color: data.color || "#6366f1",
        dueDate: data.dueDate || new Date().toISOString().split('T')[0],
        title: data.title || '',
        description: data.description || '',
        startDate: data.startDate || new Date().toISOString().split('T')[0],
        routineDays: data.routineDays || [],
        categoryId: data.categoryId || 'routine',
        icon: data.icon || '',
        notes: data.notes || '',
        isActive: data.isActive !== undefined ? data.isActive : true,
        rewardEnabled: data.rewardEnabled ?? false,
        rewardMessage: data.rewardMessage || '',
        rewardCoins: data.rewardCoins ?? 0,
        rewardItemCardIds: data.rewardItemCardIds || [],
        rewardGrantedStudentIds: data.rewardGrantedStudentIds || [],
        rewardCompletionRate: data.rewardCompletionRate ?? 100,
        includeInIncompleteCheck: data.includeInIncompleteCheck ?? false,
        lastUpdated: data.lastUpdated?.toDate() || new Date(),
        createdAt: data.createdAt?.toDate() || new Date(),
        documentNumber: data.documentNumber || 1,
        totalSize: data.totalSize || 0,
        dateAssignments: dateAssignmentsArray
      };
    }) as RoutineAssignmentConsolidated[];
    const v2 = snapshot.docs.map(docSnap => {
      const data = docSnap.data();
      let dateAssignmentsObj: { [dateId: string]: RoutineDateAssignmentV2 } = {};
      if (data.dateAssignments) {
        if (isArrayData(data.dateAssignments)) dateAssignmentsObj = dateAssignmentsArrayToObject(data.dateAssignments);
        else dateAssignmentsObj = data.dateAssignments;
      }
      return {
        ...data,
        id: docSnap.id,
        lastUpdated: data.lastUpdated?.toDate() || new Date(),
        createdAt: data.createdAt?.toDate() || new Date(),
        dateAssignments: dateAssignmentsObj
      };
    }) as RoutineAssignmentConsolidatedV2[];
    RoutineAssignmentConsolidatedService.__consolidatedCache.set(key, { v1, v2 });
    return v2;
  }

  /**
   * 문서 크기 계산
   */
  private calculateDocumentSize(data: any): number {
    return JSON.stringify(data).length;
  }

  /**
   * 새로운 통합 문서 생성 (V2: 중첩 객체 형식)
   */
  private async createNewConsolidatedDocument(
    routineId: string, 
    documentNumber: number,
    data: Partial<RoutineAssignmentConsolidated> & { dateAssignments?: RoutineDateAssignment[] | { [dateId: string]: RoutineDateAssignmentV2 } }
  ): Promise<string> {
    const docId = `${this.documentPrefix}${documentNumber}`;
    const docRef = doc(db, `users/${this.userId}/classes/${this.classId}/${this.rootCollectionName}/${routineId}/DateAssignment`, docId);
    
    // V2: 중첩 객체로 저장
    const dateAssignmentsObj = data.dateAssignments 
      ? (Array.isArray(data.dateAssignments) 
          ? dateAssignmentsArrayToObject(data.dateAssignments)
          : (data.dateAssignments as { [dateId: string]: RoutineDateAssignmentV2 }))
      : {};
    
    const consolidatedDoc = {
      id: docId,
      assignedStudentIds: data.assignedStudentIds || [],
      basePoints: data.basePoints || 10,
      classId: this.classId,
      routineAssignmentId: routineId,
      color: "#6366f1",
      dueDate: data.dueDate || new Date().toISOString().split('T')[0],
      lastUpdated: new Date(),
      title: data.title || '',
      description: data.description || '',
      startDate: data.startDate || new Date().toISOString().split('T')[0],
      routineDays: data.routineDays || [],
      categoryId: data.categoryId || 'routine',
      icon: data.icon || '',
      notes: data.notes || '',
      isActive: data.isActive !== undefined ? data.isActive : true,
      rewardEnabled: data.rewardEnabled ?? false,
      rewardMessage: data.rewardMessage || '',
      rewardCoins: data.rewardCoins ?? 0,
      rewardItemCardIds: data.rewardItemCardIds || [],
      rewardGrantedStudentIds: data.rewardGrantedStudentIds || [],
      rewardCompletionRate: data.rewardCompletionRate ?? 100,
      includeInIncompleteCheck: data.includeInIncompleteCheck ?? false,
      dateAssignments: dateAssignmentsObj,
      createdAt: new Date(),
      documentNumber,
      totalSize: 0 // 임시값, 아래에서 계산
    };

    // totalSize 계산 및 할당
    consolidatedDoc.totalSize = this.calculateDocumentSize(consolidatedDoc);

    await setDoc(docRef, {
      ...consolidatedDoc,
      lastUpdated: serverTimestamp(),
      createdAt: serverTimestamp()
    });
    this.invalidateConsolidatedCache(routineId);
    return docId;
  }

  /**
   * 루틴 과제 생성 (V2: 중첩 객체 형식)
   */
  async createRoutineAssignment(
    routineAssignment: RoutineAssignment,
    formData: RoutineAssignmentFormData
  ): Promise<string> {
    const routineId = routineAssignment.id;
    
    // V2: 빈 객체로 시작 (날짜는 멤버 데이터 생성 시 동적으로 추가)
    const dateAssignments: { [dateId: string]: RoutineDateAssignmentV2 } = {};

    // 통합 문서 생성 (V2 중첩 객체 형식)
    const documentNumber = await this.getCurrentDocumentNumber(routineId);
    const docId = await this.createNewConsolidatedDocument(routineId, documentNumber, {
      assignedStudentIds: formData.assignedStudentIds,
      basePoints: formData.basePoints,
      dueDate: formData.dueDate.toISOString().split('T')[0],
      title: formData.title,
      description: formData.description || '',
      startDate: formData.startDate.toISOString().split('T')[0],
      routineDays: formData.routineDays || [],
      categoryId: formData.categoryId || 'routine',
      icon: formData.icon || '',
      notes: formData.notes || '',
      isActive: true,
      includeInIncompleteCheck: formData.includeInIncompleteCheck ?? false,
      dateAssignments: dateAssignments as any
    });

    return docId;
  }


  /**
   * 특정 날짜의 멤버 상태 업데이트 (V2: 부분 업데이트 - dot notation)
   */
  async updateStudentStatus(
    routineId: string,
    date: string,
    studentId: string,
    status: AssignmentStatusValue,
    points: number,
    memo: string = ''
  ): Promise<void> {
    try {
      // V2: 중첩 객체 형식으로 문서 조회
      const consolidatedDocsV2 = await this.getAllConsolidatedDocumentsV2(routineId);
      
      // 해당 날짜가 포함된 문서 찾기
      let targetDocV2: RoutineAssignmentConsolidatedV2 | null = null;
      let targetDocIndex = -1;
      let dateAssignmentV2: RoutineDateAssignmentV2 | null = null;
      
      for (let i = 0; i < consolidatedDocsV2.length; i++) {
        const docV2 = consolidatedDocsV2[i];
        if (docV2.dateAssignments[date]) {
          targetDocV2 = docV2;
          targetDocIndex = i;
          dateAssignmentV2 = docV2.dateAssignments[date];
          break;
        }
      }

      // 해당 날짜가 없으면 첫 번째 문서에 동적으로 생성
      if (!targetDocV2 || !dateAssignmentV2) {
        if (consolidatedDocsV2.length === 0) {
          throw new Error(`루틴 과제 ${routineId}에 대한 통합 문서가 존재하지 않습니다.`);
        }
        
        // 첫 번째 문서에 날짜 추가
        targetDocV2 = consolidatedDocsV2[0];
        targetDocIndex = 0;
        
        // 새로운 날짜 할당 생성 (V2 중첩 객체)
        dateAssignmentV2 = {
          isCustomDate: false, // 기본값, 필요시 루틴 과제 설정에서 확인 가능
          studentStatuses: {}
        };
        
        targetDocV2.dateAssignments[date] = dateAssignmentV2;
      }

      // studentStatuses 객체가 없으면 초기화
      if (!dateAssignmentV2.studentStatuses) {
        dateAssignmentV2.studentStatuses = {};
      }
      
      // V2: 새 학생 상태 (중첩 객체)
      const studentStatusV2: RoutineStudentStatusV2 = {
        status,
        points,
        updatedAt: new Date()
      };

      // 문서 크기 확인 및 필요시 분할 (배열 형식으로 변환하여 계산)
      const targetDocArray = {
        ...targetDocV2,
        dateAssignments: dateAssignmentsObjectToArray(targetDocV2.dateAssignments)
      };
      const updatedSize = this.calculateDocumentSize(targetDocArray);

      if (updatedSize > this.maxDocumentSize) {
        // 문서 분할 로직 (배열 형식으로 변환하여 처리)
        const targetDocForSplit: RoutineAssignmentConsolidated = {
          ...targetDocV2,
          dateAssignments: dateAssignmentsObjectToArray(targetDocV2.dateAssignments)
        };
        await this.splitDocument(routineId, targetDocForSplit, targetDocIndex);
        return; // 분할 후 원본 문서 업데이트는 건너뜀
      }

      // V2: 부분 업데이트 (해당 날짜의 해당 학생만) - dot notation 사용
      const docRef = doc(db, `users/${this.userId}/classes/${this.classId}/${this.rootCollectionName}/${routineId}/DateAssignment`, targetDocV2.id);
      await updateDoc(docRef, {
        [`dateAssignments.${date}.studentStatuses.${studentId}`]: studentStatusV2,
        [`dateAssignments.${date}.isCustomDate`]: dateAssignmentV2.isCustomDate,
        totalSize: updatedSize,
        lastUpdated: serverTimestamp()
      });
      this.invalidateConsolidatedCache(routineId);

      // 보상 처리를 위해 배열 형식으로 변환하여 전달
      const consolidatedDocs = await this.getAllConsolidatedDocuments(routineId);
      await this.processRoutineRewardsForStudent(
        routineId,
        studentId,
        consolidatedDocs
      );

      console.log('✅ 루틴 과제 멤버 상태 업데이트 완료 (V2 부분 업데이트):', {
        routineId,
        date,
        studentId,
        status,
        points
      });

    } catch (error) {
      console.error('❌ 루틴 과제 멤버 상태 업데이트 실패:', error);
      throw error;
    }
  }

  /**
   * 특정 날짜의 멤버 상태를 한 번에 병합 저장 (읽기 1회 + 쓰기 1회)
   * 폴백(문서 크기 초과) 시 멤버별 updateStudentStatus를 쓰며, 그 경로에서 보상까지 이미 처리됨.
   */
  async batchUpdateStudentStatusesForDate(
    routineId: string,
    date: string,
    updates: Array<{ studentId: string; status: AssignmentStatusValue; points: number }>,
    options?: { fillMissingAssignedAsPending?: boolean; assignedStudentIds?: string[] }
  ): Promise<{ studentIdsForRewardPass: string[]; rewardsAlreadyHandled: boolean }> {
    if (!updates.length && !options?.fillMissingAssignedAsPending) {
      return { studentIdsForRewardPass: [], rewardsAlreadyHandled: false };
    }

    const fillMissing = options?.fillMissingAssignedAsPending === true;
    const assignedIds = options?.assignedStudentIds?.length
      ? options.assignedStudentIds
      : undefined;

    try {
      const consolidatedDocsV2 = await this.getAllConsolidatedDocumentsV2(routineId);

      let targetDocV2: RoutineAssignmentConsolidatedV2 | null = null;
      let targetDocIndex = -1;
      let dateAssignmentV2: RoutineDateAssignmentV2 | null = null;

      for (let i = 0; i < consolidatedDocsV2.length; i++) {
        const docV2 = consolidatedDocsV2[i];
        if (docV2.dateAssignments[date]) {
          targetDocV2 = docV2;
          targetDocIndex = i;
          dateAssignmentV2 = docV2.dateAssignments[date];
          break;
        }
      }

      if (!targetDocV2 || !dateAssignmentV2) {
        if (consolidatedDocsV2.length === 0) {
          throw new Error(`루틴 과제 ${routineId}에 대한 통합 문서가 존재하지 않습니다.`);
        }
        targetDocV2 = consolidatedDocsV2[0];
        targetDocIndex = 0;
        dateAssignmentV2 = {
          isCustomDate: false,
          studentStatuses: {}
        };
        targetDocV2.dateAssignments[date] = dateAssignmentV2;
      }

      if (!dateAssignmentV2.studentStatuses) {
        dateAssignmentV2.studentStatuses = {};
      }

      const merged: { [studentId: string]: RoutineStudentStatusV2 } = {
        ...dateAssignmentV2.studentStatuses
      };

      const rewardCheckIds = new Set<string>();

      for (const u of updates) {
        merged[u.studentId] = {
          status: u.status,
          points: u.points,
          updatedAt: new Date()
        };
        rewardCheckIds.add(u.studentId);
      }

      if (fillMissing && assignedIds?.length) {
        for (const sid of assignedIds) {
          if (merged[sid] === undefined) {
            merged[sid] = {
              status: 'pending',
              points: 0,
              updatedAt: new Date()
            };
            rewardCheckIds.add(sid);
          }
        }
      }

      const targetDocArray = {
        ...targetDocV2,
        dateAssignments: dateAssignmentsObjectToArray({
          ...targetDocV2.dateAssignments,
          [date]: {
            ...dateAssignmentV2,
            studentStatuses: merged
          }
        })
      };
      const updatedSize = this.calculateDocumentSize(targetDocArray);

      if (updatedSize > this.maxDocumentSize) {
        console.warn('⚠️ 루틴 통합 문서 크기 초과 — 배치 저장을 멤버별 경로로 폴백합니다.');
        for (const u of updates) {
          await this.updateStudentStatus(routineId, date, u.studentId, u.status, u.points, '');
        }
        if (fillMissing && assignedIds?.length) {
          for (const sid of assignedIds) {
            const fresh = await this.getAllConsolidatedDocumentsV2(routineId);
            let da: RoutineDateAssignmentV2 | null = null;
            for (const d of fresh) {
              if (d.dateAssignments[date]?.studentStatuses?.[sid] !== undefined) {
                da = d.dateAssignments[date];
                break;
              }
            }
            if (!da?.studentStatuses?.[sid]) {
              await this.updateStudentStatus(routineId, date, sid, 'pending', 0, '');
              rewardCheckIds.add(sid);
            }
          }
        }
        this.invalidateConsolidatedCache(routineId);
        return { studentIdsForRewardPass: [], rewardsAlreadyHandled: true };
      }

      const docRef = doc(
        db,
        `users/${this.userId}/classes/${this.classId}/${this.rootCollectionName}/${routineId}/DateAssignment`,
        targetDocV2.id
      );
      await updateDoc(docRef, {
        [`dateAssignments.${date}.studentStatuses`]: merged,
        [`dateAssignments.${date}.isCustomDate`]: dateAssignmentV2.isCustomDate,
        totalSize: updatedSize,
        lastUpdated: serverTimestamp()
      });
      this.invalidateConsolidatedCache(routineId);

      console.log('✅ 루틴 과제 멤버 상태 배치 업데이트 완료 (V2):', {
        routineId,
        date,
        updateCount: updates.length,
        fillMissing,
        studentCount: Object.keys(merged).length
      });

      return {
        studentIdsForRewardPass: Array.from(rewardCheckIds),
        rewardsAlreadyHandled: false
      };
    } catch (error) {
      console.error('❌ 루틴 과제 멤버 상태 배치 업데이트 실패:', error);
      throw error;
    }
  }

  /**
   * 배치 저장 직후: 멤버별 보상 로직을 순차 실행 (grant 목록 정합성을 위해 매번 최신 통합 문서를 읽음)
   */
  async runRoutineRewardsForStudentsSequential(routineId: string, studentIds: string[]): Promise<void> {
    const unique = Array.from(new Set(studentIds.filter(Boolean)));
    for (const studentId of unique) {
      const consolidatedDocs = await this.getAllConsolidatedDocuments(routineId);
      await this.processRoutineRewardsForStudent(routineId, studentId, consolidatedDocs);
    }
  }

  private calculateStudentCompletionRate(
    consolidatedDocs: RoutineAssignmentConsolidated[] | RoutineAssignmentConsolidatedV2[],
    studentId: string
  ): { totalDates: number; confirmedCount: number; completionRate: number } {
    // V2 형식인지 확인하고 배열로 변환
    let allDateAssignments: RoutineDateAssignment[] = [];
    
    if (consolidatedDocs.length > 0) {
      const firstDoc = consolidatedDocs[0];
      if ('dateAssignments' in firstDoc && !Array.isArray(firstDoc.dateAssignments)) {
        // V2 형식 - 배열로 변환
        const docsV2 = consolidatedDocs as RoutineAssignmentConsolidatedV2[];
        allDateAssignments = docsV2.flatMap(doc => dateAssignmentsObjectToArray(doc.dateAssignments));
      } else {
        // 배열 형식
        const docs = consolidatedDocs as RoutineAssignmentConsolidated[];
        allDateAssignments = docs.flatMap(doc => doc.dateAssignments || []);
      }
    }
    
    const totalDates = allDateAssignments.length;
    if (totalDates === 0) {
      return { totalDates: 0, confirmedCount: 0, completionRate: 0 };
    }

    const confirmedCount = allDateAssignments.reduce((count, dateAssignment) => {
      const studentStatus = dateAssignment.studentStatuses?.find((s: any) => s.studentId === studentId);
      return studentStatus?.status === 'confirmed' ? count + 1 : count;
    }, 0);

    const completionRate = Number(((confirmedCount / totalDates) * 100).toFixed(2));
    return { totalDates, confirmedCount, completionRate };
  }

  private async updateRewardGrantedStudentIds(
    routineId: string,
    consolidatedDocs: RoutineAssignmentConsolidated[],
    rewardGrantedStudentIds: string[]
  ): Promise<void> {
    const batch = writeBatch(db);
    consolidatedDocs.forEach((docItem) => {
      const docRef = doc(
        db,
        `users/${this.userId}/classes/${this.classId}/${this.rootCollectionName}/${routineId}/DateAssignment`,
        docItem.id
      );
      const updatedDoc = { ...docItem, rewardGrantedStudentIds };
      const totalSize = this.calculateDocumentSize(updatedDoc);
      batch.update(docRef, {
        rewardGrantedStudentIds,
        totalSize,
        lastUpdated: new Date()
      });
    });

    await batch.commit();
    this.invalidateConsolidatedCache(routineId);
  }

  private async processRoutineRewardsForStudent(
    routineId: string,
    studentId: string,
    consolidatedDocs: RoutineAssignmentConsolidated[]
  ): Promise<void> {
    try {
      if (consolidatedDocs.length === 0) {
        return;
      }

      // 호출자가 넘긴 스냅샷·메모리 캐시와 무관하게 최신 지급 목록·날짜 상태로 판단 (중복 지급 방지)
      this.invalidateConsolidatedCache(routineId);
      const docs = await this.getAllConsolidatedDocuments(routineId);
      if (docs.length === 0) {
        return;
      }

      const routine = docs[0];
      if (!routine.rewardEnabled) {
        return;
      }

      const rewardCoins = routine.rewardCoins || 0;
      const rewardItemCardIds = routine.rewardItemCardIds || [];
      const rewardCompletionRate = routine.rewardCompletionRate ?? 100;
      const hasRewards = rewardCoins > 0 || rewardItemCardIds.length > 0;
      if (!hasRewards) {
        return;
      }

      const { completionRate } = this.calculateStudentCompletionRate(docs, studentId);
      const alreadyGranted = routine.rewardGrantedStudentIds?.includes(studentId) ?? false;
      const shouldGrant = completionRate >= rewardCompletionRate;

      if (shouldGrant && !alreadyGranted) {
        const { ItemCardService } = await import('./itemCardService');
        const { StudentCoinService } = await import('./studentCoinService');
        const { userDataService } = await import('./userDataService');

        const itemCardService = new ItemCardService(this.userId, this.classId, this.memberGroup);
        const coinService = new StudentCoinService(this.userId, this.classId, this.memberGroup);

        if (rewardCoins > 0) {
          await coinService.grantCoinsToStudent(
            studentId,
            rewardCoins,
            `${routine.title} 루틴 과제 도달 보상`,
            'General'
          );
        }

        if (rewardItemCardIds.length > 0) {
          const userProfile = await userDataService.loadUserProfile(this.userId);
          if (userProfile?.userType !== 'free' && userProfile?.userType !== 'basic') {
            const itemCardSet = await itemCardService.loadItemCardSet();
            if (itemCardSet) {
              const rewardItemCards = rewardItemCardIds
                .map(itemCardId => itemCardSet.assignmentItem.find(item => item.itemCardId === itemCardId))
                .filter((item): item is any => item !== undefined);

              const studentItemCards = await itemCardService.getStudentItemCards(studentId);
              const existingRewards = studentItemCards.filter(card => card.assignmentId === routineId);

              if (existingRewards.length > 0) {
                await itemCardService.unlockStudentItemCardsByAssignment(studentId, routineId);
              }

              const newRewards = rewardItemCards.filter(rewardItem =>
                !existingRewards.some(existing => existing.itemCardId === rewardItem.itemCardId)
              );

              for (const rewardItem of newRewards) {
                const studentItemCard: any = {
                  instanceId: `routine_${routineId}_${rewardItem.itemCardId}_${Date.now()}`,
                  itemCardId: rewardItem.itemCardId,
                  type: rewardItem.type,
                  ...(rewardItem.useCount !== undefined && { remainingUseCount: rewardItem.useCount }),
                  acquiredAt: new Date(),
                  assignmentId: routineId,
                  category: 'assignment',
                  isLocked: false
                };
                await itemCardService.addStudentItemCardAtomic(studentId, studentItemCard);
              }
            }
          }
        }

        const updatedGrantedIds = [...(routine.rewardGrantedStudentIds || []), studentId];
        await this.updateRewardGrantedStudentIds(routineId, docs, updatedGrantedIds);
        userDataService.invalidateMembersCache(this.userId, this.classId, this.memberGroup);
      } else if (shouldGrant && alreadyGranted) {
        const { ItemCardService } = await import('./itemCardService');
        const { userDataService } = await import('./userDataService');

        const itemCardService = new ItemCardService(this.userId, this.classId, this.memberGroup);

        if (rewardItemCardIds.length > 0) {
          await itemCardService.unlockStudentItemCardsByAssignment(studentId, routineId);
        }

        userDataService.invalidateMembersCache(this.userId, this.classId, this.memberGroup);
      } else if (!shouldGrant && alreadyGranted) {
        const { ItemCardService } = await import('./itemCardService');
        const { StudentCoinService } = await import('./studentCoinService');
        const { userDataService } = await import('./userDataService');

        const itemCardService = new ItemCardService(this.userId, this.classId, this.memberGroup);
        const coinService = new StudentCoinService(this.userId, this.classId, this.memberGroup);

        if (rewardCoins > 0) {
          await coinService.grantCoinsToStudent(
            studentId,
            -rewardCoins,
            `${routine.title} 루틴 과제 보상 회수`,
            'General'
          );
        }

        if (rewardItemCardIds.length > 0) {
          await itemCardService.lockStudentItemCardsByAssignment(studentId, routineId);
        }

        const updatedGrantedIds = (routine.rewardGrantedStudentIds || []).filter(id => id !== studentId);
        await this.updateRewardGrantedStudentIds(routineId, docs, updatedGrantedIds);
        userDataService.invalidateMembersCache(this.userId, this.classId, this.memberGroup);
      }
    } catch (error) {
      console.error('❌ 루틴 과제 보상 처리 실패:', error);
    }
  }

  /**
   * 루틴 과제 보상 수동 지급/회수 처리 (도달률 계산값을 외부에서 전달받아 사용)
   * `consolidatedDocsParam`는 하위 호환용으로만 남김. 지급 목록은 항상 서버 최신을 사용한다.
   */
  async processRoutineRewardsManualWithRates(
    routineId: string,
    completionRatesByStudentId: Record<string, number>,
    _consolidatedDocsParam?: RoutineAssignmentConsolidated[]
  ): Promise<{
    success: boolean;
    grantedCount: number;
    revokedCount: number;
    message: string;
  }> {
    try {
      this.invalidateConsolidatedCache(routineId);
      const consolidatedDocs = await this.getAllConsolidatedDocuments(routineId);
      if (consolidatedDocs.length === 0) {
        return { success: false, grantedCount: 0, revokedCount: 0, message: '루틴 과제를 찾을 수 없습니다.' };
      }

      const routine = consolidatedDocs[0];
      if (!routine.rewardEnabled) {
        return { success: false, grantedCount: 0, revokedCount: 0, message: '루틴 과제 보상이 비활성화되어 있습니다.' };
      }

      const rewardCoins = routine.rewardCoins || 0;
      const rewardItemCardIds = routine.rewardItemCardIds || [];
      const rewardCompletionRate = routine.rewardCompletionRate ?? 100;
      const hasRewards = rewardCoins > 0 || rewardItemCardIds.length > 0;
      if (!hasRewards) {
        return { success: false, grantedCount: 0, revokedCount: 0, message: '설정된 보상이 없습니다.' };
      }

      const assignedIds = routine.assignedStudentIds || [];
      const grantedSet = new Set(routine.rewardGrantedStudentIds || []);
      let grantedCount = 0;
      let revokedCount = 0;

      const { ItemCardService } = await import('./itemCardService');
      const { StudentCoinService } = await import('./studentCoinService');
      const { userDataService } = await import('./userDataService');

      const itemCardService = new ItemCardService(this.userId, this.classId, this.memberGroup);
      const coinService = new StudentCoinService(this.userId, this.classId, this.memberGroup);

      let itemCardSet: any | null = null;
      let canGrantItemCards = rewardItemCardIds.length > 0;
      if (canGrantItemCards) {
        const userProfile = await userDataService.loadUserProfile(this.userId);
        if (userProfile?.userType === 'free' || userProfile?.userType === 'basic') {
          canGrantItemCards = false;
        } else {
          itemCardSet = await itemCardService.loadItemCardSet();
        }
      }

      const processStudent = async (studentId: string): Promise<'grant' | 'revoke' | 'none'> => {
        const completionRate = completionRatesByStudentId[studentId] ?? 0;
        const shouldGrant = completionRate >= rewardCompletionRate;
        const alreadyGranted = grantedSet.has(studentId);

        if (shouldGrant && !alreadyGranted) {
          if (rewardCoins > 0) {
            await coinService.grantCoinsToStudent(
              studentId,
              rewardCoins,
              `${routine.title} 루틴 과제 도달 보상`,
              'General'
            );
          }
          if (canGrantItemCards && itemCardSet) {
            const rewardItemCards = rewardItemCardIds
              .map(itemCardId => itemCardSet.assignmentItem.find((item: any) => item.itemCardId === itemCardId))
              .filter((item: any) => item !== undefined);
            const studentItemCards = await itemCardService.getStudentItemCards(studentId);
            const existingRewards = studentItemCards.filter(card => card.assignmentId === routineId);
            if (existingRewards.length > 0) {
              await itemCardService.unlockStudentItemCardsByAssignment(studentId, routineId);
            }
            const newRewards = rewardItemCards.filter((rewardItem: any) =>
              !existingRewards.some(existing => existing.itemCardId === rewardItem.itemCardId)
            );
            for (const rewardItem of newRewards) {
              const studentItemCard: any = {
                instanceId: `routine_${routineId}_${rewardItem.itemCardId}_${studentId}_${Date.now()}`,
                itemCardId: rewardItem.itemCardId,
                type: rewardItem.type,
                ...(rewardItem.useCount !== undefined && { remainingUseCount: rewardItem.useCount }),
                acquiredAt: new Date(),
                assignmentId: routineId,
                category: 'assignment',
                isLocked: false
              };
              await itemCardService.addStudentItemCardAtomic(studentId, studentItemCard);
            }
          }
          return 'grant';
        }
        if (!shouldGrant && alreadyGranted) {
          if (rewardCoins > 0) {
            await coinService.grantCoinsToStudent(
              studentId,
              -rewardCoins,
              `${routine.title} 루틴 과제 보상 회수`,
              'General'
            );
          }
          if (rewardItemCardIds.length > 0) {
            await itemCardService.lockStudentItemCardsByAssignment(studentId, routineId);
          }
          return 'revoke';
        }
        if (shouldGrant && alreadyGranted && rewardItemCardIds.length > 0) {
          await itemCardService.unlockStudentItemCardsByAssignment(studentId, routineId);
        }
        return 'none';
      };

      // rewardGrantedStudentIds는 루틴 DateAssignment 통합 문서(들)에만 저장. 시작 시 1회 로드 후
      // 루프에서는 grantedSet + 지급·회수 시마다 updateRewardGrantedStudentIds로 반영(멤버 수만큼 통합문서 읽기 없음).
      for (const studentId of assignedIds) {
        const action = await processStudent(studentId);
        if (action === 'grant') {
          grantedSet.add(studentId);
          grantedCount += 1;
          await this.updateRewardGrantedStudentIds(routineId, consolidatedDocs, Array.from(grantedSet));
        } else if (action === 'revoke') {
          grantedSet.delete(studentId);
          revokedCount += 1;
          await this.updateRewardGrantedStudentIds(routineId, consolidatedDocs, Array.from(grantedSet));
        }
      }

      userDataService.invalidateMembersCache(this.userId, this.classId, this.memberGroup);

      let message = `보상지급 ${grantedCount}명`;
      if (revokedCount > 0) {
        message += `, 회수 ${revokedCount}명`;
      }
      message += `\n총 보상지급자 수: ${grantedSet.size}명`;

      return { success: true, grantedCount, revokedCount, message };
    } catch (error) {
      console.error('❌ 루틴 과제 보상 수동 처리 실패:', error);
      return { success: false, grantedCount: 0, revokedCount: 0, message: '보상 처리 중 오류가 발생했습니다.' };
    }
  }

  /**
   * 루틴 과제 보상 수동 지급/회수 처리
   * 도달률 기준으로 보상 지급 및 회수 (일반 과제와 동일한 방식으로 통일)
   */
  async processRoutineRewardsManual(routineId: string): Promise<{
    success: boolean;
    grantedCount: number;
    revokedCount: number;
    message: string;
  }> {
    try {
      this.invalidateConsolidatedCache(routineId);
      const consolidatedDocs = await this.getAllConsolidatedDocuments(routineId);
      if (consolidatedDocs.length === 0) {
        return { success: false, grantedCount: 0, revokedCount: 0, message: '루틴 과제를 찾을 수 없습니다.' };
      }

      const routine = consolidatedDocs[0];
      if (!routine.rewardEnabled) {
        return { success: false, grantedCount: 0, revokedCount: 0, message: '루틴 과제 보상이 비활성화되어 있습니다.' };
      }

      const rewardCoins = routine.rewardCoins || 0;
      const rewardItemCardIds = routine.rewardItemCardIds || [];
      const rewardCompletionRate = routine.rewardCompletionRate ?? 100;
      const hasRewards = rewardCoins > 0 || rewardItemCardIds.length > 0;
      if (!hasRewards) {
        return { success: false, grantedCount: 0, revokedCount: 0, message: '설정된 보상이 없습니다.' };
      }

      const assignedIds = routine.assignedStudentIds || [];
      const grantedSet = new Set(routine.rewardGrantedStudentIds || []);
      let grantedCount = 0;
      let revokedCount = 0;

      const { ItemCardService } = await import('./itemCardService');
      const { StudentCoinService } = await import('./studentCoinService');
      const { userDataService } = await import('./userDataService');

      const itemCardService = new ItemCardService(this.userId, this.classId, this.memberGroup);
      const coinService = new StudentCoinService(this.userId, this.classId, this.memberGroup);

      let itemCardSet: any | null = null;
      let canGrantItemCards = rewardItemCardIds.length > 0;
      if (canGrantItemCards) {
        const userProfile = await userDataService.loadUserProfile(this.userId);
        if (userProfile?.userType === 'free' || userProfile?.userType === 'basic') {
          canGrantItemCards = false;
        } else {
          itemCardSet = await itemCardService.loadItemCardSet();
        }
      }

      for (const studentId of assignedIds) {
        const { completionRate } = this.calculateStudentCompletionRate(consolidatedDocs, studentId);
        const shouldGrant = completionRate >= rewardCompletionRate;
        const alreadyGranted = grantedSet.has(studentId);

        if (shouldGrant && !alreadyGranted) {
          if (rewardCoins > 0) {
            await coinService.grantCoinsToStudent(
              studentId,
              rewardCoins,
              `${routine.title} 루틴 과제 도달 보상`,
              'General'
            );
          }

          if (canGrantItemCards && itemCardSet) {
            const rewardItemCards = rewardItemCardIds
              .map(itemCardId => itemCardSet.assignmentItem.find((item: any) => item.itemCardId === itemCardId))
              .filter((item: any) => item !== undefined);

            const studentItemCards = await itemCardService.getStudentItemCards(studentId);
            const existingRewards = studentItemCards.filter(card => card.assignmentId === routineId);

            if (existingRewards.length > 0) {
              await itemCardService.unlockStudentItemCardsByAssignment(studentId, routineId);
            }

            const newRewards = rewardItemCards.filter((rewardItem: any) =>
              !existingRewards.some(existing => existing.itemCardId === rewardItem.itemCardId)
            );

            for (const rewardItem of newRewards) {
              const studentItemCard: any = {
                instanceId: `routine_${routineId}_${rewardItem.itemCardId}_${Date.now()}`,
                itemCardId: rewardItem.itemCardId,
                type: rewardItem.type,
                ...(rewardItem.useCount !== undefined && { remainingUseCount: rewardItem.useCount }),
                acquiredAt: new Date(),
                assignmentId: routineId,
                category: 'assignment',
                isLocked: false
              };
              await itemCardService.addStudentItemCardAtomic(studentId, studentItemCard);
            }
          }

          grantedSet.add(studentId);
          grantedCount += 1;
          await this.updateRewardGrantedStudentIds(routineId, consolidatedDocs, Array.from(grantedSet));
        } else if (!shouldGrant && alreadyGranted) {
          if (rewardCoins > 0) {
            await coinService.grantCoinsToStudent(
              studentId,
              -rewardCoins,
              `${routine.title} 루틴 과제 보상 회수`,
              'General'
            );
          }

          if (rewardItemCardIds.length > 0) {
            await itemCardService.lockStudentItemCardsByAssignment(studentId, routineId);
          }

          grantedSet.delete(studentId);
          revokedCount += 1;
          await this.updateRewardGrantedStudentIds(routineId, consolidatedDocs, Array.from(grantedSet));
        } else if (shouldGrant && alreadyGranted) {
          if (rewardItemCardIds.length > 0) {
            await itemCardService.unlockStudentItemCardsByAssignment(studentId, routineId);
          }
        }
      }

      userDataService.invalidateMembersCache(this.userId, this.classId, this.memberGroup);

      let message = `보상지급 ${grantedCount}명`;
      if (revokedCount > 0) {
        message += `, 회수 ${revokedCount}명`;
      }
      message += `\n총 보상지급자 수: ${grantedSet.size}명`;

      return { success: true, grantedCount, revokedCount, message };
    } catch (error) {
      console.error('❌ 루틴 과제 보상 수동 처리 실패:', error);
      return { success: false, grantedCount: 0, revokedCount: 0, message: '보상 처리 중 오류가 발생했습니다.' };
    }
  }

  /**
   * 루틴 과제 상세 조회 (날짜별 상태 포함)
   */
  async loadRoutineAssignmentWithStatuses(routineId: string): Promise<{
    routine: RoutineAssignment;
    dateStatuses: RoutineDateAssignment[];
    consolidatedDocs?: RoutineAssignmentConsolidated[];
  }> {
    try {
      const consolidatedDocs = await this.getAllConsolidatedDocuments(routineId);
      if (consolidatedDocs.length === 0) {
        throw new Error('루틴 과제를 찾을 수 없습니다.');
      }
      
      const routine = consolidatedDocs[0]; // 첫 번째 통합 문서에서 루틴 정보 가져오기
      
      // 모든 DateAssignment-X 문서의 dateAssignments 합치기
      const allDateAssignments: RoutineDateAssignment[] = [];
      consolidatedDocs.forEach(doc => {
        allDateAssignments.push(...doc.dateAssignments);
      });
      
      // RoutineAssignment 형식으로 변환 (id는 조회 경로용 routineId 사용 - 문서 id가 아닌 상위 컬렉션명)
      const routineAssignment: RoutineAssignment = {
        id: routineId,
        title: routine.title,
        description: (routine as any).description || '',
        startDate: (routine as any).startDate || new Date(),
        dueDate: new Date(routine.dueDate),
        basePoints: routine.basePoints,
        assignedStudentIds: routine.assignedStudentIds,
        routineDays: (routine as any).routineDays || [],
        icon: (routine as any).icon || '',
        notes: (routine as any).notes || '',
        isActive: (routine as any).isActive !== undefined ? (routine as any).isActive : true,
        categoryId: (routine as any).categoryId || '',
        rewardEnabled: (routine as any).rewardEnabled ?? false,
        rewardMessage: (routine as any).rewardMessage || '',
        rewardCoins: (routine as any).rewardCoins ?? 0,
        rewardItemCardIds: (routine as any).rewardItemCardIds || [],
        rewardGrantedStudentIds: (routine as any).rewardGrantedStudentIds || [],
        rewardCompletionRate: (routine as any).rewardCompletionRate ?? 100,
        includeInIncompleteCheck: (routine as any).includeInIncompleteCheck ?? false,
        createdAt: routine.createdAt,
        updatedAt: routine.lastUpdated,
        customDates: [],
        classId: routine.classId
      };
      
      return {
        routine: routineAssignment,
        dateStatuses: allDateAssignments,
        consolidatedDocs
      };
      
    } catch (error) {
      console.error('❌ 루틴 과제 상세 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 루틴 과제 삭제
   */
  async deleteRoutineAssignment(routineId: string): Promise<void> {
    try {
      // 모든 통합 문서 삭제
      const consolidatedDocs = await this.getAllConsolidatedDocuments(routineId);
      
      const batch = writeBatch(db);
      consolidatedDocs.forEach(consolidatedDoc => {
        const docRef = doc(db, `users/${this.userId}/classes/${this.classId}/${this.rootCollectionName}/${routineId}/DateAssignment`, consolidatedDoc.id);
        batch.delete(docRef);
      });

      await batch.commit();
      this.invalidateConsolidatedCache(routineId);

    } catch (error) {
      console.error('❌ 루틴 과제 통합 문서 삭제 실패:', error);
      throw error;
    }
  }

  /**
   * 문서 분할 처리
   */
  private async splitDocument(
    routineId: string,
    targetDoc: RoutineAssignmentConsolidated,
    targetDocIndex: number
  ): Promise<void> {
    try {
      // 날짜별로 정렬
      const sortedDates = [...targetDoc.dateAssignments].sort((a, b) => 
        new Date(a.date).getTime() - new Date(b.date).getTime()
      );

      // 첫 번째 절반과 두 번째 절반으로 분할
      const midIndex = Math.ceil(sortedDates.length / 2);
      const firstHalf = sortedDates.slice(0, midIndex);
      const secondHalf = sortedDates.slice(midIndex);

      // 첫 번째 문서 업데이트 (기존 문서)
      const firstDoc: RoutineAssignmentConsolidated = {
        ...targetDoc,
        dateAssignments: firstHalf,
        totalSize: this.calculateDocumentSize({ ...targetDoc, dateAssignments: firstHalf })
      };

      // 두 번째 문서 생성
      const nextDocumentNumber = await this.getCurrentDocumentNumber(routineId);
      await this.createNewConsolidatedDocument(routineId, nextDocumentNumber, {
        assignedStudentIds: targetDoc.assignedStudentIds,
        basePoints: targetDoc.basePoints,
        classId: targetDoc.classId,
        routineAssignmentId: targetDoc.routineAssignmentId,
        color: targetDoc.color,
        dueDate: targetDoc.dueDate,
        title: targetDoc.title,
        dateAssignments: secondHalf,
        createdAt: targetDoc.createdAt
      });

      // 첫 번째 문서 업데이트
      const firstDocRef = doc(db, `users/${this.userId}/classes/${this.classId}/${this.rootCollectionName}/${routineId}/DateAssignment`, firstDoc.id);
      await updateDoc(firstDocRef, {
        dateAssignments: firstDoc.dateAssignments,
        totalSize: firstDoc.totalSize,
        lastUpdated: new Date()
      });
      this.invalidateConsolidatedCache(routineId);

    } catch (error) {
      console.error('❌ 문서 분할 실패:', error);
      throw error;
    }
  }

  /**
   * 루틴 과제 업데이트 (V2: 부분 업데이트 - dot notation)
   */
  async updateRoutineAssignment(
    routineId: string,
    formData: RoutineAssignmentFormData
  ): Promise<void> {
    try {
      // V2: 중첩 객체 형식으로 문서 조회
      const consolidatedDocsV2 = await this.getAllConsolidatedDocumentsV2(routineId);
      
      if (consolidatedDocsV2.length === 0) {
        // 문서가 없으면 새로 생성
        await this.createRoutineAssignment({ id: routineId } as RoutineAssignment, formData);
        return;
      }

      // 첫 번째 문서의 메타데이터 업데이트 및 멤버 변경 처리
      const firstDocV2 = consolidatedDocsV2[0];
      
      // 멤버 배열 변경 처리 (assignedStudentIds 업데이트 이전에 수행)
      const previousStudentIds = firstDocV2.assignedStudentIds || [];
      const newStudentIds = formData.assignedStudentIds || [];
      
      // 제거된 멤버들의 상태 삭제 (모든 날짜 과제에서)
      const removedStudentIds = previousStudentIds.filter(id => !newStudentIds.includes(id));
      
      // 메타데이터 업데이트 (모든 DateAssignment-X 문서에 동일하게 적용)
      const rewardEnabled = formData.rewardEnabled ?? false;
      const rewardCompletionRate = formData.rewardCompletionRate ?? 100;
      const rewardMessage = formData.rewardMessage || '';
      const rewardCoins = formData.rewardCoins ?? 0;
      const rewardItemCardIds = formData.rewardItemCardIds || [];
      const rewardGrantedStudentIds = firstDocV2.rewardGrantedStudentIds || [];
      const includeInIncompleteCheck = formData.includeInIncompleteCheck ?? false;

      // V2: 부분 업데이트 객체 생성 (dot notation)
      const updateObj: { [key: string]: any } = {
        assignedStudentIds: formData.assignedStudentIds,
        basePoints: formData.basePoints,
        dueDate: formData.dueDate.toISOString().split('T')[0],
        title: formData.title,
        description: formData.description || '',
        startDate: formData.startDate.toISOString().split('T')[0],
        routineDays: formData.routineDays,
        categoryId: formData.categoryId || 'routine',
        icon: formData.icon || '',
        notes: formData.notes || '',
        isActive: true,
        rewardEnabled,
        rewardMessage,
        rewardCoins,
        rewardItemCardIds,
        rewardGrantedStudentIds,
        rewardCompletionRate,
        includeInIncompleteCheck,
        lastUpdated: serverTimestamp()
      };

      // 모든 DateAssignment-X 문서의 메타데이터 업데이트 및 제거된 멤버 상태 삭제
      const batch = writeBatch(db);
      for (const consolidatedDocV2 of consolidatedDocsV2) {
        const docRef = doc(db, `users/${this.userId}/classes/${this.classId}/${this.rootCollectionName}/${routineId}/DateAssignment`, consolidatedDocV2.id);
        
        // 제거된 멤버들의 상태 삭제 (dot notation 사용)
        const updateObjForDoc = { ...updateObj };
        for (const dateId in consolidatedDocV2.dateAssignments) {
          for (const removedId of removedStudentIds) {
            if (consolidatedDocV2.dateAssignments[dateId].studentStatuses?.[removedId]) {
              updateObjForDoc[`dateAssignments.${dateId}.studentStatuses.${removedId}`] = deleteField();
            }
          }
        }
        
        // 문서 크기 계산을 위해 배열 형식으로 변환
        const docForSizeCalc: RoutineAssignmentConsolidated = {
          ...consolidatedDocV2,
          dateAssignments: dateAssignmentsObjectToArray(consolidatedDocV2.dateAssignments)
        };
        updateObjForDoc.totalSize = this.calculateDocumentSize(docForSizeCalc);

        batch.update(docRef, updateObjForDoc);
      }

      await batch.commit();

      // 나머지 문서들 삭제 (필요시)
      if (consolidatedDocsV2.length > 1) {
        const batch = writeBatch(db);
        for (let i = 1; i < consolidatedDocsV2.length; i++) {
          const docRef = doc(db, `users/${this.userId}/classes/${this.classId}/${this.rootCollectionName}/${routineId}/DateAssignment`, consolidatedDocsV2[i].id);
          batch.delete(docRef);
        }
        await batch.commit();
      }
      this.invalidateConsolidatedCache(routineId);

      console.log('✅ 루틴 과제 통합 문서 업데이트 완료 (V2 부분 업데이트):', {
        routineId,
        documentCount: consolidatedDocsV2.length
      });

    } catch (error) {
      console.error('❌ 루틴 과제 통합 문서 업데이트 실패:', error);
      throw error;
    }
  }

  /**
   * 루틴 과제 날짜 계산 (사용하지 않음 - 동적 생성 방식으로 변경)
   * @deprecated 날짜 배열을 미리 생성하지 않고 멤버 데이터 생성 시 동적으로 추가
   */
  private calculateRoutineDates(formData: RoutineAssignmentFormData): Date[] {
    // 더 이상 사용하지 않음 - 동적 생성 방식으로 변경
    return [];
  }
}

export const routineAssignmentConsolidatedService = new RoutineAssignmentConsolidatedService('', '');
